import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles } from '../styles/sharedStyles';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import * as XLSX from 'xlsx';
import { formatDateTimeForDisplay, formatDateForDisplay } from '../utils/timezone';
import SearchableDropdown from '../components/SearchableDropdown';

// Helper to get the correct rate and amount
const getBillRateAndAmount = (bill) => {
  const machine = bill.machine || {};
  if (bill.rateType === 'daily') {
    const rate = parseFloat(machine.dayRate) || 0;
    return { rate, rateLabel: 'per day', amount: rate };
  } else {
    const hours = parseFloat(bill.hoursUsed) || 0;
    const rate = parseFloat(machine.hourlyRate) || 0;
    return { rate, rateLabel: 'per hour', amount: hours * rate };
  }
};

// Print-specific component
const PrintView = () => {
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const data = localStorage.getItem('reportData');
    if (data) {
      setReportData(JSON.parse(data));
    }
  }, []);

  if (!reportData) return null;

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateForDisplay(dateStr);
  };

  const formatHours = (hours, rateType) => {
    if (rateType === 'daily') return 'Daily Rate';
    if (hours == null) return 'Daily';
    if (hours === 0) return '0 hrs';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) {
      return `${wholeHours} hr${wholeHours !== 1 ? 's' : ''}`;
    } else if (wholeHours === 0) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${wholeHours}h ${minutes}m`;
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '100%', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e9f0',
        paddingBottom: '20px'
      }}>
        <img 
          src="/thumbnail_GLW Logo (new).png" 
          alt="GLW Logo" 
          style={{ height: '150px', width: 'auto', maxWidth: '450px' }} 
        />
        <div style={{ textAlign: 'right' }}>
          <h2 style={{ color: sharedStyles.colors.primary.main, margin: 0 }}>Bills Report</h2>
          <p style={{ color: '#666', margin: '5px 0 0 0' }}>
            Generated on: {formatDateTimeForDisplay(new Date())}
          </p>
        </div>
      </div>

      {/* Active Filters */}
      {Object.keys(reportData.filters).length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '10px' }}>Active Filters</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '10px'
          }}>
            {reportData.filters.customerId && (
              <div><strong>Customer:</strong> {reportData.filters.customerName}</div>
            )}
            {reportData.filters.jobId && (
                                      <div><strong>Job Name:</strong> {reportData.filters.jobTitle}</div>
            )}
            {reportData.filters.operatorId && (
              <div><strong>Operator:</strong> {reportData.filters.operatorName}</div>
            )}
            {reportData.filters.status && (
              <div><strong>Status:</strong> {reportData.filters.status}</div>
            )}
            {reportData.filters.startDate && (
              <div><strong>Start Date:</strong> {formatDate(reportData.filters.startDate)}</div>
            )}
            {reportData.filters.endDate && (
              <div><strong>End Date:</strong> {formatDate(reportData.filters.endDate)}</div>
            )}
            {reportData.filters.billNumber && (
              <div><strong>Bill Number:</strong> {reportData.filters.billNumber}</div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ 
          border: '1px solid #e5e9f0', 
          padding: '15px',
          borderRadius: '8px'
        }}>
          <div style={{ color: '#666', fontSize: '14px' }}>Total Bills</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{reportData.summary.totalBills}</div>
        </div>
        <div style={{ 
          border: '1px solid #e5e9f0', 
          padding: '15px',
          borderRadius: '8px'
        }}>
          <div style={{ color: '#666', fontSize: '14px' }}>Total Amount</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{formatCurrency(reportData.summary.totalAmount)}</div>
        </div>
        <div style={{ 
          border: '1px solid #e5e9f0', 
          padding: '15px',
          borderRadius: '8px'
        }}>
          <div style={{ color: '#666', fontSize: '14px' }}>Pending Bills</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{reportData.summary.pendingBills}</div>
        </div>
        <div style={{ 
          border: '1px solid #e5e9f0', 
          padding: '15px',
          borderRadius: '8px'
        }}>
          <div style={{ color: '#666', fontSize: '14px' }}>Paid Bills</div>
          <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{reportData.summary.paidBills}</div>
        </div>
      </div>

      {/* Table */}
      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse',
        marginTop: '20px'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Bill Number</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Customer</th>
                                    <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Job Name</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Operator</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Machine</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'left' }}>Work Date</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right' }}>Hours</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right' }}>Amount</th>
            <th style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'center' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {reportData.bills.map(bill => (
            <tr key={bill.id}>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>
                <div style={{ fontWeight: '500' }}>{bill.billNumber}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Created: {formatDate(bill.createdAt)}</div>
              </td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>{bill.customer?.companyName}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>{bill.job?.title}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>{bill.operator?.name}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>{bill.machine?.name}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}>{bill.workDate ? formatDate(bill.workDate) : 'N/A'}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right' }}>{formatHours(bill.hoursUsed, bill.rateType)}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right' }}>{(() => {
                const { amount } = getBillRateAndAmount(bill);
                return formatCurrency(amount);
              })()}</td>
              <td style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'center' }}>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: bill.status === 'Paid' ? '#e8f5e9' : 
                                 bill.status === 'Pending' ? '#fff3e0' :
                                 bill.status === 'Rejected' ? '#ffebee' : '#e3f2fd',
                  color: bill.status === 'Paid' ? '#2e7d32' :
                         bill.status === 'Pending' ? '#f57c00' :
                         bill.status === 'Rejected' ? '#d32f2f' : '#1976d2',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {bill.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: '#f8f9fa' }}>
            <td colSpan="7" style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right', fontWeight: 'bold' }}>
              Total:
            </td>
            <td style={{ padding: '12px', border: '1px solid #e5e9f0', textAlign: 'right', fontWeight: 'bold' }}>
              {formatCurrency(reportData.summary.totalAmount)}
            </td>
            <td style={{ padding: '12px', border: '1px solid #e5e9f0' }}></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const Reports = () => {
  const api = useApi();
  const { user, loading: userLoading } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [filters, setFilters] = useState({
    customerId: '',
    jobId: '',
    status: '',
    startDate: '',
    endDate: '',
    billNumber: ''
  });

  // Fetch initial data for dropdowns
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch customers for dropdown (alphabetically sorted)
        const customersResponse = await api({
          method: 'get',
          url: '/customers/dropdown'
        });
        if (customersResponse.data.success) {
          setCustomers(customersResponse.data.data);
        }

        // Fetch jobs for dropdown (alphabetically sorted)
        const jobsResponse = await api({
          method: 'get',
          url: '/jobs/dropdown'
        });
        if (jobsResponse.data.success) {
          setJobs(jobsResponse.data.data);
        }

        // Fetch machines
        const machinesResponse = await api({
          method: 'get',
          url: '/machines'
        });
        if (machinesResponse.data.success) {
          setMachines(machinesResponse.data.data.machines || machinesResponse.data.data);
        }
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Error loading initial data. Please try again.');
      }
    };

    fetchInitialData();
  }, []);

  // Fetch bills based on filters
  const fetchBills = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      
      // Log the current filters
      console.log('Current filters:', filters);
      
      // Handle each filter type specifically
      if (filters.customerId) {
        params.append('customerId', filters.customerId);
      }
      if (filters.jobId) {
        params.append('jobId', filters.jobId);
      }
      if (filters.status) {
        params.append('status', filters.status);
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }
      if (filters.billNumber) {
        params.append('billNumber', filters.billNumber);
      }
      if (filters.machineId) {
        params.append('machineId', filters.machineId);
      }

      // Log the final params
      console.log('API Request URL:', `/bills?${params.toString()}`);

      const response = await api({
        method: 'get',
        url: `/bills?${params}`
      });
      
      // Log the API response
      console.log('Bills API Response:', response.data);
      
      if (response.data.success) {
        const billsData = response.data.data.bills || response.data.data;
        console.log('Filtered bills:', billsData);
        

        
        setBills(billsData);
      } else {
        setError(response.data.message || 'Error fetching bills');
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
      setError(err.response?.data?.message || 'Error fetching bills');
    } finally {
      setLoading(false);
    }
  };

  // Fetch bills when filters change
  useEffect(() => {
    fetchBills();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Handle special cases
    if (name === 'customerId' || name === 'jobId' || name === 'machineId') {
      processedValue = value === '' ? '' : parseInt(value);
    }
    
    console.log('Setting filter:', name, processedValue);
    setFilters(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      customerId: '',
      jobId: '',
      status: '',
      startDate: '',
      endDate: '',
      billNumber: ''
    });
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateForDisplay(dateStr);
  };

  const calculateHours = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    const diffInMs = end - start;
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.round(diffInHours * 10) / 10; // Round to 1 decimal place
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Pending':
        return 'warning';
      case 'Rejected':
        return 'danger';
      case 'Approved':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Calculate total amount for filtered bills
  const totalAmount = bills.reduce((sum, bill) => {
    const amount = parseFloat(bill.amount) || 0;
    return sum + amount;
  }, 0);

  const getStatusDisplay = (status) => {
    switch (status) {
      case '15':
        return '15%';
      case '30':
        return '30%';
      case '60':
        return '60%';
      case '90':
        return '90%';
      default:
        return status;
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'In Progress':
        return 'Work is currently in progress';
      case 'On Hold':
        return 'Work has been temporarily paused';
      case 'Completed':
        return 'Work has been completed';
      case '15':
        return '15% of work completed';
      case '30':
        return '30% of work completed';
      case '60':
        return '60% of work completed';
      case '90':
        return '90% of work completed';
      default:
        return '';
    }
  };

  const handlePrint = () => {
    // Prepare report data with properly formatted filters
    const reportData = {
      filters: {
        // Only include filters that have values
        ...(filters.customerId && {
          customerId: filters.customerId,
          customerName: customers.find(c => c.id === filters.customerId)?.companyName || 'N/A'
        }),
        ...(filters.jobId && {
          jobId: filters.jobId,
          jobTitle: jobs.find(j => j.id === filters.jobId)?.title || 'N/A'
        }),
        ...(filters.status && {
          status: filters.status
        }),
        ...(filters.startDate && {
          startDate: filters.startDate
        }),
        ...(filters.endDate && {
          endDate: filters.endDate
        }),
        ...(filters.billNumber && {
          billNumber: filters.billNumber
        }),
        ...(filters.machineId && {
          machineId: filters.machineId,
          machineName: machines.find(m => m.id === filters.machineId)?.name || 'N/A'
        })
      },
      summary: {
        totalBills: bills.length,
        totalAmount: totalAmount,
        pendingBills: bills.filter(bill => bill.status === 'Pending').length,
        paidBills: bills.filter(bill => bill.status === 'Paid').length
      },
      bills: bills.map(bill => ({
        ...bill,
        customer: customers.find(c => c.id === bill.customerId),
        job: jobs.find(j => j.id === bill.jobId)
      }))
    };

    // Log the data being stored (for debugging)
    console.log('Storing report data:', reportData);

    // Save to localStorage
    localStorage.setItem('reportData', JSON.stringify(reportData));

    // Open print window using the dedicated print view HTML
    const printWindow = window.open('/print-view.html', '_blank');
    printWindow.focus();
  };

  const exportToExcel = () => {
    // Prepare data for export
    const exportData = bills.map(bill => ({
      'Bill Number': bill.billNumber,
      'Customer': bill.customer?.companyName || 'N/A',
      'Job': bill.job?.title || 'N/A',
      'Operator': 'N/A',
      'Machine': bill.machine?.name || 'N/A',
      'Bill Date': formatDate(bill.billDate),
      'Due Date': formatDate(bill.dueDate),
      'Hours': formatHours(bill.hoursUsed, bill.rateType),
      'Rate': (() => {
        const { rate, rateLabel } = getBillRateAndAmount(bill);
        return `${formatCurrency(rate)} ${rateLabel}`;
      })(),
      'Amount': (() => {
        const { amount } = getBillRateAndAmount(bill);
        return amount;
      })(),
      'Status': bill.status,
      'Created At': formatDate(bill.createdAt)
    }));

    // Add summary row
    exportData.push({
      'Bill Number': 'TOTAL',
      'Customer': '',
      'Job': '',
      'Operator': '',
      'Machine': '',
      'Bill Date': '',
      'Due Date': '',
      'Hours': '',
      'Rate': '',
      'Amount': totalAmount,
      'Status': '',
      'Created At': ''
    });

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add logo to Excel file
    const addLogoToExcel = async () => {
      try {
        // Fetch the logo image
        const response = await fetch('/thumbnail_GLW Logo (new).png');
        const arrayBuffer = await response.arrayBuffer();
        
        // Add image to worksheet
        const imageId = wb.addImage({
          buffer: arrayBuffer,
          extension: 'png',
        }, {
          tl: { col: 0, row: 0 },
          ext: { width: 450, height: 150 }
        });
        
        // Insert image at the top of the worksheet
        ws['!images'] = [imageId];
        
        // Add some spacing after the logo
        XLSX.utils.sheet_add_aoa(ws, [[''], ['']], { origin: 'A1' });
        
        // Move data down by 2 rows to make room for logo
        const range = XLSX.utils.decode_range(ws['!ref']);
        const newData = {};
        
        Object.keys(ws).forEach(key => {
          if (key !== '!ref' && key !== '!images') {
            const cell = XLSX.utils.decode_cell(key);
            const newKey = XLSX.utils.encode_cell({ r: cell.r + 2, c: cell.c });
            newData[newKey] = ws[key];
          }
        });
        
        ws['!ref'] = XLSX.utils.encode_range({
          s: { r: 0, c: 0 },
          e: { r: range.e.r + 2, c: range.e.c }
        });
        
        Object.assign(ws, newData);
        ws['!images'] = [imageId];
        
      } catch (error) {
        console.warn('Could not add logo to Excel file:', error);
      }
    };

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bills Report');

    // Add logo and save
    addLogoToExcel().then(() => {
      // Generate filename with current date
      const fileName = `Bills_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, fileName);
    });
  };

  const exportToCSV = () => {
    // Prepare headers
    const headers = [
      'Bill Number',
      'Customer',
      'Job',
      'Operator',
      'Machine',
      'Bill Date',
      'Due Date',
      'Hours',
      'Rate',
      'Amount',
      'Status',
      'Created At'
    ];

    // Prepare data rows
    const rows = bills.map(bill => [
      bill.billNumber,
      bill.customer?.companyName || 'N/A',
      bill.job?.title || 'N/A',
      'N/A',
      bill.machine?.name || 'N/A',
      formatDate(bill.billDate),
      formatDate(bill.dueDate),
      formatHours(bill.hoursUsed, bill.rateType),
      (() => {
        const { rate, rateLabel } = getBillRateAndAmount(bill);
        return `${formatCurrency(rate)} ${rateLabel}`;
      })(),
      (() => {
        const { amount } = getBillRateAndAmount(bill);
        return amount;
      })(),
      bill.status,
      formatDate(bill.createdAt)
    ]);

    // Add summary row
    rows.push([
      'TOTAL',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      totalAmount,
      '',
      ''
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => 
        typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell
      ).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Bills_Report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatHours = (hours, rateType) => {
    if (rateType === 'daily') return 'Daily Rate';
    if (hours == null) return 'Daily';
    if (hours === 0) return '0 hrs';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (minutes === 0) {
      return `${wholeHours} hr${wholeHours !== 1 ? 's' : ''}`;
    } else if (wholeHours === 0) {
      return `${minutes} min${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${wholeHours}h ${minutes}m`;
    }
  };

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const params = filters.customerId ? { customerId: filters.customerId } : {};
        const jobsResponse = await api({
          method: 'get',
          url: '/jobs/dropdown',
          params
        });
        if (jobsResponse.data.success) {
          setJobs(jobsResponse.data.data);
        } else {
          setJobs([]);
        }
      } catch (err) {
        setJobs([]);
      }
    };
    
    fetchJobs();
    // Clear job filter if customer changes
    setFilters(prev => ({ ...prev, jobId: '' }));
  }, [filters.customerId]);

  if (userLoading) {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid p-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (user?.role === 'foreman') {
    return (
      <div style={sharedStyles.pageContainer} className="container-fluid p-4">
        <div className="alert alert-warning mb-0">Bill reports are not available for your role.</div>
      </div>
    );
  }

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={sharedStyles.pageTitle}>Bills Report</h2>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-success"
            onClick={exportToExcel}
          >
            <i className="bi bi-file-earmark-excel me-2"></i>
            Export to Excel
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={exportToCSV}
          >
            <i className="bi bi-file-earmark-text me-2"></i>
            Export to CSV
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={handlePrint}
          >
            <i className="bi bi-printer me-2"></i>
            Print Report
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">Bill Number</label>
              <input
                type="text"
                className="form-control"
                name="billNumber"
                value={filters.billNumber}
                onChange={handleFilterChange}
                placeholder="Search bill number..."
              />
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Customer"
                name="customerId"
                value={filters.customerId || ''}
                onChange={handleFilterChange}
                options={customers}
                placeholder="All Customers"
                emptyMessage="No customers found"
                displayKey="companyName"
                valueKey="id"
                searchKeys={['companyName', 'contactPerson']}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Job Name"
                name="jobId"
                value={filters.jobId || ''}
                onChange={handleFilterChange}
                options={jobs}
                placeholder="All Jobs"
                emptyMessage="No jobs found"
                displayKey="title"
                valueKey="id"
                searchKeys={['title']}
                showCustomer={true}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <SearchableDropdown
                label="Machine"
                name="machineId"
                value={filters.machineId || ''}
                onChange={handleFilterChange}
                options={machines.sort((a, b) => a.name.localeCompare(b.name))}
                placeholder="All Machines"
                emptyMessage="No machines found"
                displayKey="name"
                valueKey="id"
                searchKeys={['name', 'model', 'serialNumber']}
                maxHeight="250px"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Paid">Paid</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
                <option value="15">15%</option>
                <option value="30">30%</option>
                <option value="60">60%</option>
                <option value="90">90%</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-12">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={handleResetFilters}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Reset Filters
                </button>
                <button
                  className="btn btn-primary"
                  onClick={fetchBills}
                >
                  <i className="bi bi-search me-2"></i>
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Bills</h6>
                <h3 className="mb-0">{bills.length}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Total Amount</h6>
                <h3 className="mb-0">{formatCurrency(totalAmount)}</h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Pending Bills</h6>
                <h3 className="mb-0">
                  {bills.filter(bill => bill.status === 'Pending').length}
                </h3>
              </div>
            </div>
            <div className="col-md-3">
              <div className="border rounded p-3">
                <h6 className="text-muted mb-2">Paid Bills</h6>
                <h3 className="mb-0">
                  {bills.filter(bill => bill.status === 'Paid').length}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Bills Table */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover align-middle" style={sharedStyles.table}>
              <thead className="table-light">
                <tr>
                  <th>Bill Number</th>
                  <th>Customer</th>
                <th>Job Name</th>
                <th>Bill Date</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : bills.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      <i className="bi bi-receipt mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <p className="mb-0">No bills found</p>
                    </td>
                  </tr>
                ) : (
                  bills.map(bill => (
                    <tr key={bill.id}>
                      <td>
                        <div className="fw-medium">{bill.billNumber}</div>
                        <small className="text-muted">Created: {formatDate(bill.createdAt)}</small>
                      </td>
                      <td>
                        <div className="fw-medium">{bill.customer?.companyName}</div>
                        <small className="text-muted">ID: {bill.customerId}</small>
                      </td>
                      <td>
                        <div className="fw-medium">{bill.job?.title}</div>
                        <small className="text-muted">ID: {bill.jobId}</small>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{bill.workDate ? formatDate(bill.workDate) : 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span>{bill.dueDate ? formatDate(bill.dueDate) : 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="fw-medium">{formatCurrency(bill.amount)}</div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className={`badge bg-${getStatusBadgeClass(bill.status)}`}>
                            {getStatusDisplay(bill.status)}
                          </span>
                          {getStatusDescription(bill.status) && (
                            <small className="text-muted mt-1">
                              {getStatusDescription(bill.status)}
                            </small>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {bills.length > 0 && (
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="7" className="text-end fw-bold">Total:</td>
                    <td className="fw-bold">{formatCurrency(totalAmount)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports; 